import { ReactNode, useEffect, useState } from 'react';
import Highlighter from 'react-highlighter';

import * as Popover from '@radix-ui/react-popover';

import { CheckIcon, ChevronDownIcon } from '@/assets/icons';
import { Input, InputProps } from '@/components/atoms';
import { lockBodyScroll } from '@/utils/scrollLock';
import { cn } from '@/utils/tailwind';

export type FilterDropdownOption = {
  label: string;
  value: string;
  [key: string]: any;
};

type FilterDropdownProps = Omit<InputProps, 'onChange' | 'value'> & {
  value: FilterDropdownOption | null;
  onChange: (value: FilterDropdownOption | null) => void;
  placeholder?: string;
  options: FilterDropdownOption[];
  disabled?: boolean;
  label?: ReactNode;
  triggerClassName?: string;
  hideChevronIcon?: boolean;
  optional?: boolean;
  clearable?: boolean;
};

const FilterDropdown = ({
  value,
  onChange,
  placeholder,
  options,
  disabled,
  label,
  triggerClassName,
  hideChevronIcon,
  clearable = false,
  optional = false,
  ...props
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    return lockBodyScroll();
  }, [isOpen]);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleClear = () => {
    onChange(null);
    setSearch('');
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={disabled ? undefined : setIsOpen}>
      <Popover.Trigger asChild>
        <div className={cn('w-auto', triggerClassName)}>
          <Input
            className={cn(
              'cursor-pointer w-fit',
              isOpen ? 'outline-2 outline-custom-cyan-400' : 'outline outline-custom-warm-grey-200'
            )}
            placeholder={placeholder}
            value={isOpen ? search : (value?.label ?? '')}
            onChange={e => {
              const { value } = e.target;

              setSearch(value);
              if (!isOpen) setIsOpen(true);
            }}
            disabled={disabled}
            label={label}
            onClear={handleClear}
            clearable={clearable}
            optional={optional}
            rightIcon={
              hideChevronIcon ? null : (
                <ChevronDownIcon
                  width={20}
                  height={20}
                  className={cn('transition-transform', isOpen ? 'rotate-180' : 'rotate-0')}
                />
              )
            }
            {...props}
          />
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          onOpenAutoFocus={e => e.preventDefault()}
          className={cn(
            'PopoverContent',
            'bg-custom-warm-grey-25 border border-custom-warm-grey-200 rounded-[12px] shadow-custom-select p-1 mt-1 w-full z-50 max-h-64 overflow-y-auto',
            'animate-in fade-in zoom-in-95 slide-in-from-top-2',
            'focus:outline-none'
          )}
          sideOffset={4}
          align="start"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => (
              <button
                key={option.value}
                className={cn(
                  "relative bg-transparent flex justify-between w-full cursor-pointer items-center gap-3 rounded-sm px-3 py-2 outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 hover:bg-custom-warm-grey-100",
                  'data-[state=checked]:bg-custom-warm-grey-150 mb-1 last:mb-0',
                  { 'bg-custom-warm-grey-150': value?.value === option.value }
                )}
                onClick={() => {
                  onChange(option);
                  setSearch('');
                  setIsOpen(false);
                }}
              >
                <p className="text-body-14-regular text-custom-warm-grey-700 text-left">
                  <Highlighter search={search ?? ''}>{option.label}</Highlighter>
                </p>
                {value?.value === option.value && (
                  <CheckIcon className="size-3 text-custom-cyan-500" />
                )}
              </button>
            ))
          ) : (
            <div className="text-custom-warm-grey-400 px-3 py-2 text-sm">No options found</div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export { FilterDropdown };
